import { useEffect, useState, useCallback, useRef, useMemo } from 'react';

import { useNavigate, Link, useParams } from 'react-router-dom';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, Search, Toolbar, VirtualScroll } from '@syncfusion/ej2-react-grids';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';

import SearchBar from '../utils/SearchBar';
import CustomSelect from '../utils/CustomSelect';
import { formatCurrency } from '../utils/Format';

const PjTemplate = (props) => props.PJ ? (
    <i className="text-blue-500 fa-paperclip fas"></i>
) : null;

const ArchiverTemplate = (props) => props.Archiver_Enr ? (
    <i className="text-blue-500 fa-square-check fas"></i>
) : (
    <i className="w-15 h-15 text-blue-500 fa-square far"></i>
);

const PartageTemplate = (props) => props.Web_Publier ? (
    <i className="text-blue-500 fa-square-check fas"></i>
) : (
    <i className="w-15 h-15 text-blue-500 fa-square far"></i>
);

const statusTemplate = (props) => {
    const normalized = props.Etat?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
    const colors = {
        'ouvert': 'bg-green-500',
        'cloture': 'bg-red-500',
        'a suivre': 'bg-yellow-500',
        'pre-rapport': 'bg-blue-500',
        'devis valide': 'bg-purple-500',
        'sans suite': 'bg-red-700',
    };
    let color = 'bg-gray-400';
    for (const [k, v] of Object.entries(colors)) if (normalized.startsWith(k)) { color = v; break; }
    return <span className={`px-2 py-1 rounded text-white ${color}`}>{props.Etat}</span>;
};


const ListDeclaration = ({ lstDeclarations, columns = [], ExclusivePage = '', loading = false, showAdd = true }) => {
    const navigate = useNavigate();
    const routeParams = useParams();

    const siteUUID = routeParams?.uuid || null;
    if (columns.length === 0) {
        columns = [
            { field: "Date_Declaration", headerText: "Date Declaration", type: "date", format: "dd/MM/yyyy", width: "130px" },
            { field: "Libelle", headerText: "Libellé", width: "400px", cssClass: "font-bold" },
            { field: "PJ", headerText: "PJ", width: "85px", textAlign: "Center", template: PjTemplate },
            { field: "Archiver_Enr", headerText: "Archiver", width: "90px", textAlign: "Center", template: ArchiverTemplate },
            { field: "Etat", headerText: "État", width: "130px", template: statusTemplate },
            { field: "Type_Declaration", headerText: "Type", width: "130px" },
            { field: "Code_Procedure", headerText: "Code", width: "130px" },
            { field: "Charge_affaire", headerText: "CA", width: "180px" },
            { field: "ID_DOSSIER", headerText: "ID Dossier", width: "180px" },
            { field: "Expert_Ref", headerText: "Expert Réf.", width: "180px" },
            { field: "Etat_Motif", headerText: "Motif", width: "200px" },
            { field: "Origine_de_la_demande", headerText: "Orig. demande", width: "160px" },
            { field: "Date_Sinistre", headerText: "Date Sinistre", type: "date", format: "dd/MM/yyyy", width: "130px" },
            { field: "Type_Ouvrage", headerText: "Ouvrage", width: "200px" },
            { field: "Type_Marche", headerText: "Marché", width: "200px" },
            { field: "Cout_tt_Pathologie", headerText: "Coût Pathologie", width: "180px", format: "C2", textAlign: "Right" },
            { field: "Montant_Du_Definitif", headerText: "Coût Déclaration", width: "180px", format: "C2", textAlign: "Right" },
            { field: "Date_creation", headerText: "Création", type: "date", format: "dd/MM/yyyy", width: "130px" },
        ];
    }

    const [query, setQuery] = useState("");

    const norm = (s) =>
        (s ?? "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const SEARCH_FIELDS = [
        "Libelle",
        "Type_Declaration",
        "Code_Procedure",
        "ID_DOSSIER",
        "Charge_affaire",
        "Expert_Ref",
        "Assureur_Client_Ref",
        "Courtier_Ref",
        "Assureur_Interne_Ref",
        "Origine_de_la_demande",
        "Type_Ouvrage",
        "Type_Marche",
    ];

    const emptyMessageTemplate = useCallback(() => {
        if (loading) {
            return (
                <div>
                    {Array.from({ length: 15 }).map((_, i) => (
                        <div key={i} className="flex flex-row gap-4 p-3 border-b-2 text-gray-500">
                            {Array.from({ length: 5 }).map((_, j) => (
                                <div key={j} className="bg-gray-200 rounded-md h-4 animate-pulse" style={{ width: `${Math.random() * 30 + 10}rem` }}></div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="p-4 text-gray-500 text-start">Aucun résultat trouvé.</div>;
    }, [loading]);

    const gridRef = useRef();

    const [filtersVisible, setFiltersVisible] = useState(false);

    const [selectedSociete, setSelectedSociete] = useState("Toutes");
    const [selectedEntite, setSelectedEntite] = useState("Toutes");
    const [selectedEtat, setSelectedEtat] = useState("Toutes");
    const [selectedType, setSelectedType] = useState("Toutes");
    const [selectedTypeOuvrage, setSelectedTypeOuvrage] = useState("Toutes");
    const [selectedTypeMarche, setSelectedTypeMarche] = useState("Toutes");

    const [selectedMinDate, setSelectedMinDate] = useState(null);
    const [selectedMaxDate, setSelectedMaxDate] = useState(null);

    const [showWithCost, setShowWithCost] = useState(false);
    const [hideArchived, setHideArchived] = useState(false);
    const [hideCompleted, setHideCompleted] = useState(false);

    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        let filtered = lstDeclarations;

        if (query.trim()) {
            const q = norm(query);
            filtered = filtered.filter((d) =>
                SEARCH_FIELDS.some((f) => norm(d?.[f]).includes(q))
            );
        }

        if (selectedSociete !== "Toutes") { filtered = filtered.filter(declaration => declaration.Societe === selectedSociete); }
        if (selectedEntite !== "Toutes") { filtered = filtered.filter(declaration => declaration.Entite === selectedEntite); }
        if (selectedEtat !== "Toutes") { filtered = filtered.filter(declaration => declaration.Etat === selectedEtat); }
        if (selectedType !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Declaration === selectedType); }
        if (selectedTypeOuvrage !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Ouvrage === selectedTypeOuvrage); }
        if (selectedTypeMarche !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Marche === selectedTypeMarche); }

        if (selectedMinDate) {
            filtered = filtered.filter(declaration => new Date(declaration.Date_Declaration) >= selectedMinDate);
        }
        if (selectedMaxDate) {
            filtered = filtered.filter(declaration => new Date(declaration.Date_Declaration) <= selectedMaxDate);
        }

        if (showWithCost) filtered = filtered.filter(d => d.Cout_tt_Pathologie > 0 || d.Montant_Du_Definitif > 0);
        if (hideArchived) filtered = filtered.filter(d => !d.Archiver_Enr);
        if (hideCompleted) {
            const completedStates = ["cloture", "sans suite"];
            filtered = filtered.filter(d => !completedStates.some(state => d.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').startsWith(state)));
        }

        setFilteredData(filtered);
    }, [query, selectedSociete, selectedEntite, selectedEtat, selectedType, selectedTypeMarche, selectedTypeOuvrage, selectedMaxDate, selectedMinDate, showWithCost, hideArchived, hideCompleted, lstDeclarations]);

    const optSociete = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Societe).filter(societe => societe))];
    const optEntite = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Entite).filter(entite => entite))];
    const optEtat = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Etat).filter(etat => etat))];
    const optType = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Declaration).filter(type => type))];
    const optTypeOuvrage = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Ouvrage).filter(type => type))];
    const optTypeMarche = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Marche).filter(type => type))];

    const handleRowDoubleClick = (event) => {
        const clickedRowData = event.rowData;
        console.log(clickedRowData);
        const uuid = clickedRowData.UUID_;
        if (uuid) navigate(`/declarations/${uuid}`);
    };

    const formattedData = useMemo(() => {
        return filteredData.map(declaration => ({
            ...declaration,
            Cout_tt_Pathologie: formatCurrency(declaration.Cout_tt_Pathologie),
            Montant_Du_Definitif: formatCurrency(declaration.Montant_Du_Definitif)
        }));
    }, [filteredData]);

    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.dataSource = formattedData;
            gridRef.current.dataBind();
        }
    }, [formattedData]);

    // console.log(filteredData);

    return (
        <div className="bg-white p-4 rounded-lg">
            <div className="flex lg:flex-row flex-col justify-between items-start lg:items-center mx-auto my-4 lg:my-8 px-6 pb-4 border-gray-300 border-b container">
                <div>
                    <h4 className="font-bold text-gray-800 text-2xl leading-tight">Déclarations</h4>
                    <ul className="flex md:flex-row flex-col items-start md:items-center mt-3 text-gray-600 text-sm">
                        <li className="flex items-center mt-3 md:mt-0 mr-3">
                            <span className="mr-2">
                                <i class="fa-solid fa-scroll"></i>
                            </span>
                            <div className="flex items-center gap-1">
                                {loading ? (
                                    <div className="bg-gray-200 rounded-md w-9 h-4 animate-pulse"></div>
                                ) : (
                                    <span>{filteredData.length}</span>
                                )}
                                {' '}{filteredData.length === 1 ? 'Déclaration' : 'Déclarations'}
                            </div>
                        </li>
                    </ul>
                </div>
                {showAdd && (
                    <div className="mt-6 lg:mt-0">
                        <Link
                            to="/declarations/create"
                            state={siteUUID ? { siteUUID: siteUUID } : null}
                            className="bg-orange-600 hover:bg-orange-500 px-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 text-white text-sm transition duration-300 ease-in-out"
                        >
                            Ajouter une Déclaration
                        </Link>

                    </div>
                )}
            </div>
            <div className="lg:mx-auto mb-4 lg:mb-8 px-6 container">
                <div className="flex justify-between mb-4">
                    <button
                        onClick={() => setFiltersVisible(!filtersVisible)}
                        className="flex items-center text-gray-500 hover:text-orange-600 text-sm"
                    >
                        {filtersVisible ? (
                            <i className="fa-chevron-up fas"></i>
                        ) : (
                            <i className="fa-chevron-down fas"></i>
                        )}
                        <span className="ml-2">{filtersVisible ? 'Réduire' : 'Afficher'} les filtres</span>
                    </button>
                    <div className="w-[20rem]">
                        <SearchBar
                            value={query}
                            onChange={setQuery}
                            onClear={() => setQuery("")}
                            placeholder="Rechercher …"
                            disabled={loading}
                        />
                    </div>
                </div>
                {filtersVisible && (
                    <>
                        {ExclusivePage === 'SiteDetail' ? (
                            <div id="cont_filtre" className="flex xl:flex-row flex-col gap-4 pb-6 w-full">
                                <div className="flex gap-4 w-full xl:w-1/2">
                                    <CustomSelect
                                        label={"État"}
                                        options={optEtat}
                                        value={selectedEtat}
                                        onChange={setSelectedEtat}
                                    />
                                    <CustomSelect
                                        label={"Type de Déclaration"}
                                        options={optType}
                                        value={selectedType}
                                        onChange={setSelectedType}
                                    />
                                </div>
                                <div className="flex flex-row xl:flex-wrap justify-center xl:gap-0 xl:*:mt-6">
                                    <label class="mcui-checkbox">
                                        <input type="checkbox" checked={showWithCost} onChange={() => setShowWithCost(!showWithCost)} />
                                        <div>
                                            <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                            </svg>
                                        </div>
                                        <div class="text-gray-600 text-lg">Avec Coût</div>
                                    </label>
                                    <label class="mcui-checkbox">
                                        <input type="checkbox" checked={hideArchived} onChange={() => setHideArchived(!hideArchived)} />
                                        <div>
                                            <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                            </svg>
                                        </div>
                                        <div class="text-gray-600 text-lg">Masquer les Archives</div>
                                    </label>
                                    <label class="mcui-checkbox">
                                        <input type="checkbox" checked={hideCompleted} onChange={() => setHideCompleted(!hideCompleted)} />
                                        <div>
                                            <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                            </svg>
                                        </div>
                                        <div class="text-gray-600 text-lg">Masquer les Terminés</div>
                                    </label>
                                </div>
                            </div>
                        )
                            : (
                                <div id="cont_filtre" className="flex xl:flex-row flex-col gap-4 pb-6 w-full">
                                    <div className="flex md:flex-row flex-col gap-4 w-full xl:w-1/2">
                                        <div className="w-full">
                                            <CustomSelect
                                                label={"Société"}
                                                options={optSociete}
                                                value={selectedSociete}
                                                onChange={setSelectedSociete}
                                            />
                                            <CustomSelect
                                                label={"Entité/Région"}
                                                options={optEntite}
                                                value={selectedEntite}
                                                onChange={setSelectedEntite}
                                            />
                                        </div>
                                        <div className="w-full">
                                            <CustomSelect
                                                label={"État"}
                                                options={optEtat}
                                                value={selectedEtat}
                                                onChange={setSelectedEtat}
                                            />
                                            <CustomSelect
                                                label={"Type de Déclaration"}
                                                options={optType}
                                                value={selectedType}
                                                onChange={setSelectedType}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex md:flex-row flex-col items-center gap-4 w-full xl:w-1/2 md:full">
                                        <div className="w-full">
                                            <CustomSelect
                                                label={"Type de Marché"}
                                                options={optTypeMarche}
                                                value={selectedTypeMarche}
                                                onChange={setSelectedTypeMarche}
                                            />
                                            <CustomSelect
                                                label={"Type d'Ouvrage"}
                                                options={optTypeOuvrage}
                                                value={selectedTypeOuvrage}
                                                onChange={setSelectedTypeOuvrage}
                                            />
                                        </div>
                                        <div className="flex flex-col justify-between w-full h-full">
                                            <div>
                                                <span className="text-gray-600 text-sm">À partir du :</span>
                                                <DatePickerComponent
                                                    placeholder="Trier par Date Minimal"
                                                    format={"dd/MM/yyyy"}
                                                    value={selectedMinDate}
                                                    onChange={(e) => setSelectedMinDate(e.value)}
                                                />
                                            </div>
                                            <div>
                                                <span className="text-gray-600 text-sm">Jusqu'au :</span>
                                                <DatePickerComponent
                                                    placeholder="Trier par Date Maximal"
                                                    format={"dd/MM/yyyy"}
                                                    value={selectedMaxDate}
                                                    onChange={(e) => setSelectedMaxDate(e.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-row xl:flex-col justify-center gap-10 xl:gap-0 w-full xl:w-1/4">
                                        <label class="mcui-checkbox">
                                            <input type="checkbox" checked={showWithCost} onChange={() => setShowWithCost(!showWithCost)} />
                                            <div>
                                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                                </svg>
                                            </div>
                                            <div class="text-gray-600 text-lg">Avec Coût</div>
                                        </label>
                                        <label class="mcui-checkbox">
                                            <input type="checkbox" checked={hideArchived} onChange={() => setHideArchived(!hideArchived)} />
                                            <div>
                                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                                </svg>
                                            </div>
                                            <div class="text-gray-600 text-lg">Masquer les Archives</div>
                                        </label>
                                        <label class="mcui-checkbox">
                                            <input type="checkbox" checked={hideCompleted} onChange={() => setHideCompleted(!hideCompleted)} />
                                            <div>
                                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                                </svg>
                                            </div>
                                            <div class="text-gray-600 text-lg">Masquer les Terminés</div>
                                        </label>
                                    </div>
                                </div>
                            )
                        }
                    </>

                )}
                <div className="border-2 border-gray-300 hover:border-orange-400 rounded w-full transition duration-300 ease-in-out grow">
                    <GridComponent
                        ref={gridRef}
                        dataSource={formattedData}
                        recordDoubleClick={handleRowDoubleClick ? handleRowDoubleClick : null}
                        emptyRecordTemplate={emptyMessageTemplate}
                        allowSorting={true}
                        allowReordering={true}
                        enableStickyHeader={true}
                        enableVirtualization={true}
                        loadingIndicator={{ indicatorType: 'Shimmer' }}
                        height={520}
                        sortSettings={{
                            columns: [{ field: 'Date_creation', direction: 'Descending' }]
                        }}
                    >
                        <ColumnsDirective>
                            {columns.map(col => (
                                <ColumnDirective key={col.field} {...col} />
                            ))}
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter, Search, Toolbar, VirtualScroll]} />
                    </GridComponent>
                </div>
            </div>
        </div>
    );
};

export default ListDeclaration;
